(function (global) {
  'use strict';

  var MAX_FEED_PAGES = 100;
  var PAGE_TIMEOUT_MS = 20000;
  var RUNTIME_KEY = '__fmdDealsRuntime';

  function isActive(startSeconds, endSeconds, nowMilliseconds) {
    var start = Number(startSeconds) * 1000;
    var end = Number(endSeconds) * 1000;
    var now = Number(nowMilliseconds);
    return Number.isFinite(start) && Number.isFinite(end) && Number.isFinite(now) &&
      start > 0 && end > start && now >= start && now < end;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { isActive: isActive };
  if (!global || !global.document) return;

  // Shopify can evaluate a section asset again after a section reload. Reuse
  // the first runtime so listeners, timers, and the feed cache stay singular.
  if (global[RUNTIME_KEY] && global[RUNTIME_KEY].version === 3) {
    global[RUNTIME_KEY].scan();
    return;
  }

  var document = global.document;
  var states = new Map();
  var feedCache = new Map();
  var chatObserver = null;

  function attr(node, name) {
    return node && node.getAttribute ? node.getAttribute(name) : null;
  }

  function now() {
    var value = Date.now();
    return Number.isFinite(value) ? value : NaN;
  }

  function numberAttribute(node, name, fallback) {
    var value = Number(attr(node, name));
    return Number.isInteger(value) && value > 0 ? value : fallback;
  }

  function normalizeHandle(value) {
    return String(value || '').trim().toLowerCase();
  }

  function readCategories(value) {
    var result = [];
    var seen = new Set();
    String(value || '').trim().split(/\s+/).forEach(function (part) {
      var handle = normalizeHandle(part);
      if (handle && !seen.has(handle)) {
        seen.add(handle);
        result.push(handle);
      }
    });
    return result;
  }

  function absoluteURL(raw) {
    var URLCtor = global.URL || (typeof URL !== 'undefined' ? URL : null);
    if (!URLCtor) throw new Error('URL support is unavailable');
    var base = global.location && global.location.href ? global.location.href : document.baseURI;
    var url = new URLCtor(String(raw || ''), base);
    if (global.location && global.location.origin && url.origin !== global.location.origin) {
      throw new Error('Deals feed must be same-origin');
    }
    url.hash = '';
    return url;
  }

  function feedBaseURL(raw) {
    var url = absoluteURL(raw);
    url.searchParams.delete('page');
    return url;
  }

  function feedPageURL(base, page) {
    var URLCtor = global.URL || (typeof URL !== 'undefined' ? URL : null);
    var url = new URLCtor(base.href);
    url.searchParams.set('page', String(page));
    return url;
  }

  function requestPage(url, controller) {
    if (typeof global.fetch !== 'function') return Promise.reject(new Error('Fetch is unavailable'));

    var timer;
    var options = {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    };
    if (controller) options.signal = controller.signal;

    var request;
    try {
      request = global.fetch(url.href, options);
    } catch (error) {
      return Promise.reject(error);
    }

    var timeout = new Promise(function (_, reject) {
      timer = global.setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('Deals feed request timed out'));
      }, PAGE_TIMEOUT_MS);
    });

    // The deadline covers headers AND the complete response body. Racing only
    // fetch() leaves a stalled body pending forever after headers arrive.
    var body = Promise.resolve(request).then(function (response) {
      if (!response || response.ok === false || (Number(response.status) >= 400)) {
        throw new Error('Deals feed returned an invalid response');
      }
      if (response.url) {
        var responseURL = absoluteURL(response.url);
        if (responseURL.origin !== url.origin) throw new Error('Deals feed redirected off-origin');
      }
      if (typeof response.text !== 'function') throw new Error('Deals feed body is unavailable');
      return response.text();
    });
    return Promise.race([body, timeout]).finally(function () {
      global.clearTimeout(timer);
    });
  }

  function parseInteger(value) {
    var parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : NaN;
  }

  function parseFeedPage(html, requestedPage) {
    var Parser = global.DOMParser || (typeof DOMParser !== 'undefined' ? DOMParser : null);
    if (!Parser) throw new Error('Deals feed parser is unavailable');
    var parsed = new Parser().parseFromString(String(html || ''), 'text/html');
    var feed = parsed && parsed.querySelector('[data-fmd-deals-feed]');
    if (!feed || attr(feed, 'data-valid') !== 'true') {
      throw new Error('Deals feed validation failed');
    }

    var page = parseInteger(attr(feed, 'data-page'));
    var pages = parseInteger(attr(feed, 'data-pages'));
    if (page !== requestedPage || page < 1 || pages < 1 || pages > MAX_FEED_PAGES || page > pages) {
      throw new Error('Deals feed pagination is inconsistent');
    }

    var records = [];
    var cards = feed.querySelectorAll('[data-fmd-deal]');
    for (var index = 0; index < cards.length; index += 1) {
      var card = cards[index];
      var id = String(attr(card, 'data-deal-id') || '').trim();
      var start = attr(card, 'data-start');
      var end = attr(card, 'data-end');
      if (!id || !Number.isFinite(Number(start)) || !Number.isFinite(Number(end)) ||
        Number(start) <= 0 || Number(end) <= Number(start)) {
        throw new Error('Deals feed contains an invalid offer');
      }
      records.push({
        id: id,
        start: start,
        end: end,
        categories: readCategories(attr(card, 'data-categories')),
        node: card
      });
    }

    return { page: page, pages: pages, records: records };
  }

  function loadAllPages(base, controller) {
    return (async function () {
      var expectedPages = null;
      var seenPages = new Set();
      var records = new Map();

      for (var requestedPage = 1; requestedPage <= MAX_FEED_PAGES; requestedPage += 1) {
        var url = feedPageURL(base, requestedPage);
        var html = await requestPage(url, controller);
        var result = parseFeedPage(html, requestedPage);

        if (expectedPages === null) expectedPages = result.pages;
        if (result.pages !== expectedPages || seenPages.has(result.page)) {
          throw new Error('Deals feed pagination changed while loading');
        }
        seenPages.add(result.page);

        result.records.forEach(function (record) {
          // The first occurrence wins. A repeated ID is never rendered twice.
          if (!records.has(record.id)) records.set(record.id, record);
        });

        if (requestedPage === expectedPages) {
          return { records: Array.from(records.values()), pages: expectedPages };
        }
        if (requestedPage >= MAX_FEED_PAGES) {
          throw new Error('Deals feed exceeds the safe page limit');
        }
      }

      throw new Error('Deals feed did not complete');
    }());
  }

  function feedEntryFor(rawURL) {
    var base = feedBaseURL(rawURL);
    var key = base.href;
    var entry = feedCache.get(key);
    if (entry) {
      entry.refs += 1;
      return entry;
    }

    var AbortControllerCtor = global.AbortController;
    var controller = AbortControllerCtor ? new AbortControllerCtor() : null;
    entry = { key: key, refs: 1, pending: true, controller: controller, data: null };
    // A pending entry is abortable only when it has no section consumers.
    entry.promise = loadAllPages(base, controller).then(function (data) {
      entry.pending = false;
      entry.data = data;
      entry.controller = null;
      return data;
    }, function (error) {
      entry.pending = false;
      if (feedCache.get(entry.key) === entry) feedCache.delete(entry.key);
      throw error;
    });
    feedCache.set(key, entry);
    return entry;
  }

  function releaseFeedEntry(entry) {
    if (!entry) return;
    entry.refs = Math.max(0, entry.refs - 1);
    if (entry.pending && entry.refs === 0 && feedCache.get(entry.key) === entry) {
      feedCache.delete(entry.key);
      if (entry.controller) entry.controller.abort();
    }
  }

  function invalidateFeed(rawURL) {
    try {
      var key = feedBaseURL(rawURL).href;
      var entry = feedCache.get(key);
      if (entry) {
        feedCache.delete(key);
        if (entry.pending && entry.controller) entry.controller.abort();
      }
    } catch (_) {
      // The subsequent load reports the useful same-origin/URL error.
    }
  }

  function replaceChildren(node, children) {
    if (typeof node.replaceChildren === 'function') {
      node.replaceChildren.apply(node, children);
      return;
    }
    while (node.firstChild) node.removeChild(node.firstChild);
    children.forEach(function (child) { node.appendChild(child); });
  }

  function focusWithoutScroll(node) {
    if (!node || node.hidden || node.disabled || typeof node.focus !== 'function') return;
    try {
      node.focus({ preventScroll: true });
    } catch (_) {
      node.focus();
    }
  }

  function closestWithin(target, selector, root) {
    if (!target || typeof target.closest !== 'function') return null;
    var found = target.closest(selector);
    return found && root.contains(found) ? found : null;
  }

  function allButton(state) {
    for (var index = 0; index < state.buttons.length; index += 1) {
      if (normalizeHandle(attr(state.buttons[index], 'data-deal-filter')) === 'all') {
        return state.buttons[index];
      }
    }
    return null;
  }

  function setSelectedButtonState(state) {
    var selected = null;
    state.buttons.forEach(function (button) {
      var handle = normalizeHandle(attr(button, 'data-deal-filter'));
      if (!selected && handle === state.selectedCategory) selected = button;
    });
    state.buttons.forEach(function (button) {
      button.setAttribute('aria-pressed', button === selected ? 'true' : 'false');
    });
    return selected;
  }

  function setErrorVisibility(state, visible) {
    if (!state.errorElement) return;
    state.errorElement.hidden = !visible;
    var retry = state.errorElement.querySelector('[data-deal-retry]');
    if (retry) retry.disabled = !visible || state.loading;
    if (!visible) return;

    var fallback = state.errorElement.querySelector('a');
    if (fallback && state.dealsURL) fallback.setAttribute('href', state.dealsURL);
  }

  function selectedQuery(state) {
    if (!state.pageMode || !global.location || !global.location.search) return 'all';
    var Params = global.URLSearchParams || (typeof URLSearchParams !== 'undefined' ? URLSearchParams : null);
    if (!Params) return 'all';
    var value = normalizeHandle(new Params(global.location.search).get('fmd_category'));
    if (!value || value === 'all') return 'all';
    return state.knownCategories.has(value) ? value : 'all';
  }

  function dealsURLFor(state) {
    var raw = attr(state.root, 'data-deals-url');
    if (raw) {
      try { return feedBaseURL(raw).href; } catch (_) { return raw; }
    }
    var feed = attr(state.root, 'data-feed-url');
    if (!feed) return '';
    try {
      var url = feedBaseURL(feed);
      url.searchParams.set('view', 'fmd-deals');
      return url.href;
    } catch (_) {
      return '';
    }
  }

  function updateViewAllLink(state) {
    if (state.pageMode || !state.viewAll || !state.dealsURL) return;
    try {
      var url = absoluteURL(state.dealsURL);
      url.hash = '';
      if (state.selectedCategory === 'all') url.searchParams.delete('fmd_category');
      else url.searchParams.set('fmd_category', state.selectedCategory);
      state.viewAll.setAttribute('href', url.pathname + (url.search || ''));
    } catch (_) {
      // An invalid optional link must not prevent the in-place filter.
    }
  }

  function currentActiveRecords(state, currentTime) {
    if (!state.data || !Number.isFinite(currentTime)) return [];
    return state.data.records.filter(function (record) {
      return !state.removedIds.has(record.id) && isActive(record.start, record.end, currentTime);
    });
  }

  function pruneExpiredRecords(state, currentTime) {
    if (!state.data || !Number.isFinite(currentTime)) return;
    var changed = false;
    var records = state.data.records.filter(function (record) {
      if (state.removedIds.has(record.id)) {
        changed = true;
        return false;
      }
      if (Number(record.end) * 1000 <= currentTime) {
        state.removedIds.add(record.id);
        changed = true;
        return false;
      }
      return true;
    });
    if (changed) state.data.records = records;
  }

  function categorySignature(records) {
    return records.map(function (record) { return record.id; }).sort().join('|');
  }

  function updateFilters(state, activeRecords) {
    var activeCategories = new Set();
    activeRecords.forEach(function (record) {
      record.categories.forEach(function (category) { activeCategories.add(category); });
    });

    var selectedWasActive = state.selectedCategory === 'all' || activeCategories.has(state.selectedCategory);
    if (!selectedWasActive) {
      var recoverFocus = state.userSelected;
      state.selectedCategory = 'all';
      state.userSelected = false;
      state.displayCount = state.displayLimit;
      if (recoverFocus) {
        state.focusAll = true;
        state.nextAnnouncement = 'No active deals remain in that category. Showing all deals.';
      }
    }

    var selectedButton = null;
    state.buttons.forEach(function (button) {
      var handle = normalizeHandle(attr(button, 'data-deal-filter'));
      var available = handle === 'all' || activeCategories.has(handle);
      button.hidden = handle !== 'all' && !available;
      button.disabled = !state.loaded || button.hidden;
      if (!selectedButton && handle === state.selectedCategory) selectedButton = button;
    });
    state.buttons.forEach(function (button) {
      button.setAttribute('aria-pressed', button === selectedButton ? 'true' : 'false');
    });
    if (state.filters) state.filters.hidden = !state.loaded || !selectedButton;
    return activeCategories;
  }

  function cloneRecord(state, record) {
    var clone = document.importNode ? document.importNode(record.node, true) : record.node.cloneNode(true);
    if (!state.pageMode && clone.querySelectorAll) {
      clone.querySelectorAll('h2').forEach(function (heading) {
        var replacement = document.createElement('h3');
        Array.prototype.slice.call(heading.attributes || []).forEach(function (attribute) {
          replacement.setAttribute(attribute.name, attribute.value);
        });
        replacement.innerHTML = heading.innerHTML;
        heading.parentNode.replaceChild(replacement, heading);
      });
    }
    return clone;
  }

  function syncEmptyAndTerms(state, count) {
    if (state.empty) state.empty.hidden = count > 0;
    if (state.terms) state.terms.hidden = count === 0;
  }

  function writeStatus(state, message) {
    if (state.status && state.status.textContent !== message) state.status.textContent = message;
  }

  function setStatus(state, count, total) {
    if (!state.status) return;
    if (state.loading) {
      writeStatus(state, 'Loading deals…');
      return;
    }
    if (state.hasError) {
      writeStatus(state, 'Deals could not be refreshed. Showing available offers.');
      return;
    }
    if (state.nextAnnouncement) {
      writeStatus(state, state.nextAnnouncement);
      state.nextAnnouncement = '';
      return;
    }
    if (!count) {
      writeStatus(state, state.selectedCategory === 'all' ?
        'No active deals are available right now.' : 'No active deals remain in this category.');
      return;
    }
    var matching = Number.isInteger(total) ? total : count;
    var shownCopy = matching > count ? 'Showing ' + count + ' of ' + matching : String(count);
    writeStatus(state, state.selectedCategory === 'all' ?
      shownCopy + ' active deal' + (matching === 1 ? '' : 's') + '.' :
      shownCopy + ' active deal' + (matching === 1 ? '' : 's') + ' in this category.');
  }

  function renderState(state, activeRecords) {
    if (!state.loaded || !state.grid) return;
    updateFilters(state, activeRecords);
    var matching = state.selectedCategory === 'all' ? activeRecords : activeRecords.filter(function (record) {
      return record.categories.indexOf(state.selectedCategory) !== -1;
    });
    var visibleLimit = state.pageMode ? state.displayCount : state.displayLimit;
    var visible = matching.slice(0, visibleLimit);
    var children = visible.map(function (record) { return cloneRecord(state, record); });
    replaceChildren(state.grid, children);
    state.renderedIds = new Set(visible.map(function (record) { return record.id; }));
    state.matchingCount = matching.length;
    state.categorySignature = categorySignature(activeRecords);
    state.grid.setAttribute('aria-busy', 'false');
    syncEmptyAndTerms(state, visible.length);

    if (state.more) {
      state.more.hidden = !state.pageMode || matching.length <= visible.length;
      state.more.disabled = state.more.hidden;
    }
    setStatus(state, visible.length, matching.length);
    updateViewAllLink(state);

    if (state.focusAll) {
      state.focusAll = false;
      focusWithoutScroll(allButton(state));
    }
  }

  function visibleServerCardCount(state) {
    return state.grid ? state.grid.querySelectorAll('[data-fmd-deal]').length : 0;
  }

  function pruneServerCards(state) {
    if (!state.grid) return;
    var currentTime = now();
    var seen = new Set();
    state.grid.querySelectorAll('[data-fmd-deal]').forEach(function (card, index) {
      if (!isActive(attr(card, 'data-start'), attr(card, 'data-end'), currentTime)) {
        card.remove();
        return;
      }
      var id = String(attr(card, 'data-deal-id') || '').trim();
      if (id && seen.has(id)) {
        card.remove();
        return;
      }
      seen.add(id || '__server-card-' + index);
    });
    syncEmptyAndTerms(state, visibleServerCardCount(state));
  }

  function prepareLoading(state) {
    state.loading = true;
    state.hasError = false;
    setErrorVisibility(state, false);
    if (state.grid) state.grid.setAttribute('aria-busy', 'true');
    state.buttons.forEach(function (button) { button.disabled = true; });
    if (state.more) {
      state.more.hidden = true;
      state.more.disabled = true;
    }
    setStatus(state, visibleServerCardCount(state));
  }

  function finishError(state) {
    state.loading = false;
    state.hasError = true;
    if (state.grid) state.grid.setAttribute('aria-busy', 'false');
    state.buttons.forEach(function (button) {
      button.disabled = true;
      button.setAttribute('aria-pressed', 'false');
    });
    if (state.filters) state.filters.hidden = true;
    if (state.more) {
      state.more.hidden = true;
      state.more.disabled = true;
    }
    setErrorVisibility(state, true);
    syncEmptyAndTerms(state, visibleServerCardCount(state));
    setStatus(state, visibleServerCardCount(state));
  }

  function beginLoad(state, retry) {
    if (state.destroyed || state.loadPromise) return;
    if (retry) {
      if (state.feedEntry) {
        releaseFeedEntry(state.feedEntry);
        state.feedEntry = null;
      }
      invalidateFeed(attr(state.root, 'data-feed-url'));
    }

    prepareLoading(state);
    var rawURL = attr(state.root, 'data-feed-url');
    try {
      state.feedEntry = feedEntryFor(rawURL);
    } catch (error) {
      finishError(state);
      return;
    }

    state.loadPromise = state.feedEntry.promise.then(function (data) {
      if (state.destroyed) return;
      state.data = data;
      state.loaded = true;
      state.loading = false;
      state.hasError = false;
      state.displayCount = state.displayLimit;
      state.categorySignature = '';
      state.grid.setAttribute('aria-busy', 'false');
      if (state.pagination) state.pagination.hidden = true;
      setErrorVisibility(state, false);
      renderState(state, currentActiveRecords(state, now()));
    }).catch(function () {
      if (!state.destroyed) finishError(state);
    }).then(function () {
      state.loadPromise = null;
    });
  }

  function refreshState(state) {
    if (!state || state.destroyed || !state.root.isConnected) return;
    var currentTime = now();
    if (!state.loaded) {
      pruneServerCards(state);
      return;
    }

    var previousSignature = state.categorySignature;
    pruneExpiredRecords(state, currentTime);
    var active = currentActiveRecords(state, currentTime);
    var signature = categorySignature(active);
    if (signature !== previousSignature) {
      renderState(state, active);
      return;
    }

    var visibleCount = visibleServerCardCount(state);
    syncEmptyAndTerms(state, visibleCount);
    setStatus(state, visibleCount, state.matchingCount);
  }

  function handleRootClick(state, event) {
    var retry = closestWithin(event.target, '[data-deal-retry]', state.root);
    if (retry) {
      event.preventDefault();
      if (!state.loading) beginLoad(state, true);
      return;
    }

    var more = closestWithin(event.target, '[data-deal-more]', state.root);
    if (more) {
      event.preventDefault();
      if (state.loaded && state.pageMode && !state.loading && !more.disabled) {
        state.displayCount += state.displayLimit;
        renderState(state, currentActiveRecords(state, now()));
      }
      return;
    }

    var button = closestWithin(event.target, '[data-deal-filter]', state.root);
    if (!button || !state.loaded || state.loading || button.disabled || button.hidden) return;
    var handle = normalizeHandle(attr(button, 'data-deal-filter'));
    if (!handle || (handle !== 'all' && !state.knownCategories.has(handle))) return;
    event.preventDefault();
    state.selectedCategory = handle === 'all' ? 'all' : handle;
    state.userSelected = true;
    state.displayCount = state.displayLimit;
    renderState(state, currentActiveRecords(state, now()));
  }

  function handleExpiredClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var card = target.closest('[data-fmd-deal]');
    if (!card) return;
    var root = target.closest('[data-fmd-deals]');
    var state = root && states.get(root);
    if (!state || !state.grid.contains(card)) return;
    if (!isActive(attr(card, 'data-start'), attr(card, 'data-end'), now())) {
      event.preventDefault();
      refreshState(state);
    }
  }

  function destroyState(state) {
    if (!state || state.destroyed) return;
    state.destroyed = true;
    if (state.timer) global.clearInterval(state.timer);
    if (state.root) state.root.removeEventListener('click', state.clickHandler);
    if (state.feedEntry) releaseFeedEntry(state.feedEntry);
    states.delete(state.root);
  }

  function initRoot(root) {
    if (!root || !root.matches || !root.matches('[data-fmd-deals]')) return;
    if (states.has(root)) destroyState(states.get(root));
    var grid = root.querySelector('[data-deal-grid]');
    if (!grid) return;

    var state = {
      root: root,
      grid: grid,
      pageMode: attr(root, 'data-page-mode') === 'true' || root.classList.contains('fmd-deals--page'),
      displayLimit: numberAttribute(root, 'data-display-limit', 24),
      filters: root.querySelector('[data-deal-filters]'),
      buttons: Array.prototype.slice.call(root.querySelectorAll('[data-deal-filter]')),
      status: root.querySelector('[data-deal-status]'),
      errorElement: root.querySelector('[data-deal-error]'),
      empty: root.querySelector('[data-deal-empty]'),
      terms: root.querySelector('[data-deal-terms]'),
      more: root.querySelector('[data-deal-more]'),
      pagination: root.querySelector('[data-deals-pagination]'),
      viewAll: root.querySelector('[data-view-all-deals]'),
      dealsURL: '',
      knownCategories: new Set(),
      selectedCategory: 'all',
      displayCount: 24,
      loaded: false,
      loading: false,
      hasError: false,
      matchingCount: 0,
      destroyed: false,
      data: null,
      feedEntry: null,
      loadPromise: null,
      removedIds: new Set(),
      renderedIds: new Set(),
      categorySignature: '',
      userSelected: false,
      focusAll: false,
      nextAnnouncement: ''
    };
    state.displayCount = state.displayLimit;
    state.buttons.forEach(function (button) {
      var handle = normalizeHandle(attr(button, 'data-deal-filter'));
      if (handle && handle !== 'all') state.knownCategories.add(handle);
    });
    state.dealsURL = dealsURLFor(state);
    state.selectedCategory = selectedQuery(state);
    states.set(root, state);

    state.clickHandler = function (event) { handleRootClick(state, event); };
    root.addEventListener('click', state.clickHandler);
    if (state.filters) state.filters.hidden = true;
    state.buttons.forEach(function (button) {
      button.disabled = true;
      button.hidden = false;
    });
    state.selectedCategory = state.selectedCategory || 'all';
    setSelectedButtonState(state);
    setErrorVisibility(state, false);
    if (state.more) {
      state.more.hidden = true;
      state.more.disabled = true;
    }
    writeStatus(state, 'Loading deals…');
    state.grid.setAttribute('aria-busy', 'true');
    pruneServerCards(state);
    state.timer = global.setInterval(function () { refreshState(state); }, 1000);
    beginLoad(state, false);
  }

  function scan() {
    document.querySelectorAll('[data-fmd-deals]').forEach(function (root) {
      if (!states.has(root)) initRoot(root);
    });
    observeDealActions();
  }

  function sectionRoot(target) {
    if (!target) return null;
    if (target.matches && target.matches('[data-fmd-deals]')) return target;
    return target.closest ? target.closest('[data-fmd-deals]') : null;
  }

  function observeDealActions() {
    var body = document.body;
    if (!body) return;
    if (chatObserver) chatObserver.disconnect();
    body.classList.remove('fmd-deals-in-view');
    if (!('IntersectionObserver' in global)) return;

    var visibleSections = new Set();
    chatObserver = new global.IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) visibleSections.add(entry.target);
        else visibleSections.delete(entry.target);
      });
      body.classList.toggle('fmd-deals-in-view', visibleSections.size > 0);
    });
    document.querySelectorAll('[data-fmd-deals]:not(.fmd-deals--page)').forEach(function (section) {
      chatObserver.observe(section);
    });
  }

  function refreshAll() {
    Array.from(states.values()).forEach(function (state) {
      if (!state.root.isConnected) destroyState(state);
      else refreshState(state);
    });
    observeDealActions();
  }

  function onSectionLoad(event) {
    var root = sectionRoot(event.target);
    if (root) initRoot(root);
    else scan();
    observeDealActions();
  }

  function onSectionUnload(event) {
    var root = sectionRoot(event.target);
    if (root && states.has(root)) destroyState(states.get(root));
    observeDealActions();
  }

  document.addEventListener('click', handleExpiredClick, true);
  document.addEventListener('visibilitychange', refreshAll);
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  global.addEventListener('pageshow', refreshAll);

  var runtime = {
    version: 3,
    scan: scan,
    destroy: function () {
      Array.from(states.values()).forEach(destroyState);
      if (chatObserver) chatObserver.disconnect();
      document.removeEventListener('click', handleExpiredClick, true);
      document.removeEventListener('visibilitychange', refreshAll);
      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      global.removeEventListener('pageshow', refreshAll);
      delete global[RUNTIME_KEY];
    }
  };
  global[RUNTIME_KEY] = runtime;
  global.fmdDealsInitialized = true;
  scan();
}(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this)));
